'use client'

import { useState } from 'react'
import Image from 'next/image'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Shield,
  Check,
  ChevronDown,
  ChevronUp,
  Monitor,
  BookOpen,
  Mic,
  Brain,
  GraduationCap,
  FileText,
  BarChart3,
  Zap,
  Star,
  Award,
  Heart,
  Target,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Palette,
  Globe,
  MessageCircle,
} from 'lucide-react'

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5, ease: 'easeOut' },
  }),
}

// ============================================================
// ENTREGAVEIS DETALHADOS
// ============================================================

const entregaveis = [
  {
    categoria: 'Plataforma e Posicionamento Digital',
    icon: Monitor,
    cor: 'rose',
    items: [
      { nome: 'Sistema web com area de membros', desc: 'Plataforma com login/senha, gestao de usuarios e ambiente seguro para seus clientes acessarem cursos e conteudos' },
      { nome: 'Landing pages ilimitadas', desc: 'Paginas de captura, vendas, webinario e obrigado com design profissional otimizado para conversao' },
      { nome: 'Integracoes diversas', desc: 'Pagamento (Assas), email marketing, analytics, webhooks e integracao completa' },
      { nome: 'Blog profissional integrado', desc: 'Sistema de blog SEO integrado a plataforma para posicionamento no Google sobre gestao de marca' },
    ],
  },
  {
    categoria: 'Inteligencia Artificial',
    icon: Brain,
    cor: 'purple',
    items: [
      { nome: 'Instalacao e treinamento de Claude Code', desc: 'Setup completo da IA no seu computador com treinamento hands-on para voce usar no dia a dia' },
      { nome: 'Segundo cerebro no Obsidian', desc: 'Vault estruturado com seus conhecimentos, Metodo Marca Vivo e conteudos organizados para consulta e producao' },
      { nome: 'Passaporte de Contexto Marca Vivo', desc: 'Documento completo que mapeia seu conhecimento sobre gestao de marca, design emocional e experiencia do cliente' },
    ],
  },
  {
    categoria: 'Curso Completo',
    icon: GraduationCap,
    cor: 'blue',
    items: [
      { nome: 'Cronograma completo do curso', desc: 'Estrutura modular com sequencia pedagogica baseada no Metodo Marca Vivo (Alma, Corpo, Presenca, Core)' },
      { nome: 'Roteiros em 4 formatos', desc: 'Cada aula produzida em slide (apresentacao), HTML (visual), audio (narracao TTS) e teleprompter (gravacao)' },
      { nome: 'Conteudo baseado no Metodo Marca Vivo', desc: 'Curso estruturado sobre branding, design emocional, jornada do cliente e cultura de marca' },
    ],
  },
  {
    categoria: 'Livro Autoral',
    icon: BookOpen,
    cor: 'amber',
    items: [
      { nome: 'Criacao do livro completo', desc: 'Estruturacao, escrita assistida por IA com sua voz autoral, revisao gramatical e editorial' },
      { nome: 'Diagramacao profissional', desc: 'Layout 14x21cm para grafica com capa, sumario, ficha catalografica e folha de rosto' },
      { nome: 'Impressao de 2 copias', desc: 'Duas unidades fisicas impressas em grafica profissional para voce ter em maos' },
    ],
  },
  {
    categoria: 'Funil e Vendas',
    icon: Target,
    cor: 'rose',
    items: [
      { nome: 'Funil evergreen completo', desc: 'Estrutura de webinario automatizado que vende 24h por dia sem voce precisar estar ao vivo' },
      { nome: 'Escopo de palestra presencial', desc: 'Roteiro estruturado para palestra de 60-90min sobre Marca Vivo, pronto para eventos e SEBRAE' },
      { nome: 'Programa de mentoria', desc: 'Estrutura completa baseada no curso e livro, com cronograma de encontros e materiais de apoio' },
    ],
  },
  {
    categoria: 'Marketing de Conteudo',
    icon: BarChart3,
    cor: 'emerald',
    items: [
      { nome: 'Blog com 60 artigos', desc: 'Conteudo SEO sobre gestao de marca, experiencia do cliente, design emocional e cultura empresarial' },
      { nome: 'Podcast com resumo do livro/curso', desc: 'Episodios em audio profissional que condensam os principais conceitos do Metodo Marca Vivo' },
    ],
  },
]

// ============================================================
// CRONOGRAMA
// ============================================================

const cronograma = [
  {
    semana: 'Semanas 1-2',
    fase: 'Descoberta e Estruturacao',
    items: ['Passaporte de Contexto Marca Vivo', 'Instalacao Claude Code + Obsidian', 'Cronograma do curso', 'Estrutura do livro'],
    cor: 'rose',
  },
  {
    semana: 'Semanas 3-6',
    fase: 'Producao de Conteudo',
    items: ['Roteiros do curso (4 formatos)', 'Escrita do livro capitulo a capitulo', 'Roteiro da palestra', 'Primeiros artigos do blog'],
    cor: 'blue',
  },
  {
    semana: 'Semanas 7-9',
    fase: 'Plataforma e Revisao',
    items: ['Deploy da plataforma com marca Dani Morais', 'Area de membros + landing pages', 'Revisao e diagramacao do livro', 'Integracao de pagamentos'],
    cor: 'purple',
  },
  {
    semana: 'Semanas 10-12',
    fase: 'Publicacao e Lancamento',
    items: ['Livro impresso (2 copias)', 'Blog completo (60 artigos)', 'Podcast publicado', 'Funil evergreen ativo', 'Programa de mentoria estruturado'],
    cor: 'amber',
  },
]

// ============================================================
// COMPARATIVO DE VALOR
// ============================================================

const comparativo = [
  { item: 'Plataforma web personalizada', valorMercado: 'R$ 8.000 - R$ 15.000' },
  { item: 'Curso com roteiros profissionais', valorMercado: 'R$ 3.000 - R$ 6.000' },
  { item: 'Livro autoral + diagramacao + impressao', valorMercado: 'R$ 4.000 - R$ 8.000' },
  { item: 'Blog com 60 artigos SEO', valorMercado: 'R$ 6.000 - R$ 12.000' },
  { item: 'Funil evergreen + webinario', valorMercado: 'R$ 3.000 - R$ 5.000' },
  { item: 'Podcast profissional', valorMercado: 'R$ 2.000 - R$ 4.000' },
  { item: 'Setup IA + segundo cerebro', valorMercado: 'R$ 2.000 - R$ 3.000' },
  { item: 'Palestra + programa de mentoria', valorMercado: 'R$ 2.000 - R$ 4.000' },
]

// ============================================================
// PLANOS DE PAGAMENTO
// ============================================================

const planos = [
  {
    id: 'dani-entrada',
    nome: 'Entrada via PIX',
    subtitulo: 'Primeira parcela do projeto',
    preco: 10000,
    precoCentavos: 1000000,
    parcelas: 1,
    parcelaValor: '10.000',
    cor: 'rose',
    destaque: true,
    desconto: false,
    icon: Zap,
    badge: 'Sinal para iniciar',
  },
]

const ASSINATURA_SEMANAL_URL = 'https://www.asaas.com/i/3g3qbv3c7khilg45'

// ============================================================
// FAQ
// ============================================================

const faq = [
  {
    pergunta: 'Em quanto tempo o projeto fica pronto?',
    resposta: 'O cronograma previsto e de 12 semanas (3 meses). Comecamos pela estruturacao do conteudo e terminamos com tudo publicado e funcionando. Voce acompanha cada etapa e aprova antes de avancar.',
  },
  {
    pergunta: 'Preciso ter conhecimento tecnico?',
    resposta: 'Nao. Toda a parte tecnica (plataforma, integracoes, deploy) fica por nossa conta. O que precisamos de voce e o conhecimento sobre gestao de marca e o Metodo Marca Vivo. O Passaporte de Contexto captura tudo isso de forma estruturada.',
  },
  {
    pergunta: 'O sistema e meu? Tenho controle total?',
    resposta: 'Sim. A plataforma fica no seu dominio, com seu login admin, sua marca. Voce tem controle total sobre usuarios, conteudo e configuracoes. Nao e um aluguel, e um ativo seu.',
  },
  {
    pergunta: 'E se eu quiser mudar algo depois?',
    resposta: 'A plataforma e construida com codigo aberto (Next.js, React, Supabase). Qualquer desenvolvedor pode dar manutencao. Alem disso, com o Claude Code instalado, voce mesma pode fazer ajustes guiados pela IA.',
  },
  {
    pergunta: 'Os 60 artigos sao genericos?',
    resposta: 'Nao. Cada artigo e produzido com base no seu Passaporte de Contexto, otimizado para SEO e escrito com tecnicas anti-IA para garantir autenticidade. Sao artigos que ranqueiam no Google e geram trafego organico sobre gestao de marca.',
  },
  {
    pergunta: 'Como funciona o pagamento?',
    resposta: 'O investimento total e de R$ 20.000. Voce paga uma entrada de R$ 10.000 (PIX ou cartao em ate 10x) e o restante e dividido em 5 parcelas semanais de R$ 2.000 via link de pagamento.',
  },
  {
    pergunta: 'E se eu nao gostar do resultado?',
    resposta: 'Cada entrega e aprovada por voce antes de avancar. Nao existe surpresa no final. Se em algum ponto o resultado nao te atender, ajustamos ate ficar do jeito que voce precisa. O seu nivel de exigencia e o nosso padrao.',
  },
  {
    pergunta: 'Voces ja fizeram algo parecido?',
    resposta: 'Sim. A plataforma e baseada no mesmo sistema que roda o eBuz (app.ebuz.com.br) e o Negocios S/A (app.antoniofogaca.com.br) - com area de membros, cursos publicados, blog e integracoes. Tambem produzimos 6 livros com diagramacao profissional e mais de 140 aulas.',
  },
  {
    pergunta: 'O que e o Passaporte de Contexto?',
    resposta: 'E um documento que mapeia todo o seu conhecimento especializado usando a Metodologia APE (Arquitetura de Produtos Educacionais). Ele serve como base para TODOS os conteudos: curso, livro, artigos, palestra. Garante consistencia e profundidade em tudo que e produzido.',
  },
]

// ============================================================
// COMPONENTE
// ============================================================

const totalMercado = 'R$ 30.000 a R$ 57.000'

export default function DaniPropostaPage() {
  const [faqAberto, setFaqAberto] = useState<number | null>(null)

  const corClasses: Record<string, { bg: string; border: string; text: string; badge: string; btn: string; btnHover: string; light: string }> = {
    rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700', btn: 'bg-rose-600', btnHover: 'hover:bg-rose-700', light: 'bg-rose-50' },
    blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', btn: 'bg-blue-600', btnHover: 'hover:bg-blue-700', light: 'bg-blue-50' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', btn: 'bg-purple-600', btnHover: 'hover:bg-purple-700', light: 'bg-purple-50' },
    amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', btn: 'bg-amber-600', btnHover: 'hover:bg-amber-700', light: 'bg-amber-50' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', btn: 'bg-emerald-600', btnHover: 'hover:bg-emerald-700', light: 'bg-emerald-50' },
  }

  return (
    <main className="min-h-screen bg-[#f8fafb]">
      {/* Header */}
      <header className="w-full py-4 px-6 border-b border-gray-100 bg-white">
        <div className="mx-auto max-w-5xl flex items-center gap-3">
          <Image
            src="/images/jonathan-machado.jpg"
            alt="Jonathan Machado"
            width={40}
            height={40}
            className="rounded-full"
          />
          <div className="flex flex-col">
            <span className="text-lg font-semibold text-gray-900 leading-tight">Jonathan Machado</span>
            <span className="text-xs text-gray-400">Proposta Comercial - Daniella Morais</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-4xl px-4 pt-10 pb-6 md:pt-16 text-center">
        <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 px-4 py-1.5 text-sm font-medium text-rose-700 mb-6">
            <Shield className="h-3.5 w-3.5" />
            Proposta Exclusiva - Daniella Morais
          </span>
        </motion.div>

        <motion.h1
          className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={1}
        >
          Seu Metodo Merece
          <br />
          <span className="text-rose-600">um Ecossistema a Altura</span>
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto mb-6"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={2}
        >
          Curso, livro, blog, podcast, funil evergreen e programa de mentoria.
          Tudo construido sobre o Metodo Marca Vivo e seus 22 anos de experiencia em gestao de marca.
        </motion.p>

        <motion.div
          className="flex flex-wrap items-center justify-center gap-4 md:gap-6 text-sm text-gray-500"
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          custom={3}
        >
          <span className="flex items-center gap-1.5">
            <Monitor className="h-4 w-4 text-rose-500" />
            Plataforma propria
          </span>
          <span className="flex items-center gap-1.5">
            <BookOpen className="h-4 w-4 text-amber-500" />
            Livro impresso
          </span>
          <span className="flex items-center gap-1.5">
            <GraduationCap className="h-4 w-4 text-blue-500" />
            Curso completo
          </span>
          <span className="flex items-center gap-1.5">
            <Globe className="h-4 w-4 text-emerald-500" />
            60 artigos SEO
          </span>
          <span className="flex items-center gap-1.5">
            <Mic className="h-4 w-4 text-purple-500" />
            Podcast
          </span>
        </motion.div>
      </section>

      {/* Contexto do Projeto */}
      <section className="mx-auto max-w-4xl px-4 pb-8">
        <motion.div
          className="bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Seu cenario atual</h3>
          <div className="space-y-4 text-sm text-gray-700 leading-relaxed">
            <p>
              Voce e <span className="font-semibold text-gray-900">consultora de marca</span> com
              22 anos de carreira e criadora do{' '}
              <span className="font-semibold text-gray-900">Metodo Marca Vivo</span>.
              Levou uma faculdade de 800 para 4.600 alunos (Uniplan) usando branding estrategico,
              e gerou R$ 750 mil em 3 meses no varejo com redesign de experiencia do cliente.
            </p>
            <p>
              Seu metodo ja esta validado na pratica com resultados concretos.
              O que falta e a <span className="font-semibold text-rose-700">infraestrutura digital completa</span>{' '}
              para transformar esse conhecimento em produtos escaláveis: um curso estruturado, um livro autoral,
              presenca SEO, funil de vendas e programa de mentoria.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="bg-rose-50 rounded-xl p-4 border border-rose-100">
                <p className="font-semibold text-rose-800 text-xs uppercase tracking-wide mb-1">Metodo</p>
                <p className="text-rose-700">Marca Vivo: Alma, Corpo, Presenca e Core. Design emocional aplicado a gestao de marca e experiencia do cliente.</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="font-semibold text-blue-800 text-xs uppercase tracking-wide mb-1">Resultados</p>
                <p className="text-blue-700">800 para 4.600 alunos (Uniplan). R$ 750k em 3 meses no varejo. 22 anos de carreira em branding estrategico.</p>
              </div>
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <p className="font-semibold text-amber-800 text-xs uppercase tracking-wide mb-1">Oportunidade</p>
                <p className="text-amber-700">Metodo validado sem plataforma, sem livro, sem curso digital, sem funil. Conhecimento preso no presencial.</p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Oportunidade */}
      <section className="mx-auto max-w-4xl px-4 pb-12">
        <motion.div
          className="bg-gradient-to-br from-rose-50 to-pink-50 border-2 border-rose-200 rounded-2xl p-6 md:p-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <div className="flex items-start gap-4">
            <div className="bg-rose-100 rounded-xl p-3 shrink-0">
              <AlertTriangle className="h-6 w-6 text-rose-600" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                O mercado de consultoria de marca esta em transformacao
              </h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Experts com metodo validado que nao digitalizam agora serao substituidos por IA e agencias genericas.
                Voce tem 22 anos de experiencia, resultados comprovados e um metodo proprio. Mas sem plataforma, sem livro,
                sem curso e sem presenca SEO, esse conhecimento fica limitado ao alcance presencial.
              </p>
              <p className="text-sm text-rose-700 font-medium">
                Quem digitaliza primeiro se posiciona como referencia. Quem espera, vira mais uma opcao.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Entregaveis */}
      <section className="mx-auto max-w-5xl px-4 pb-16" id="entregaveis">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          O que esta incluso no projeto
        </motion.h2>
        <motion.p
          className="text-center text-gray-500 mb-10 max-w-2xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Cada item abaixo e um entregavel concreto. Sem surpresas, sem letras miudas.
        </motion.p>

        <div className="space-y-6">
          {entregaveis.map((grupo, gi) => {
            const GrupoIcon = grupo.icon
            const cores = corClasses[grupo.cor]
            return (
              <motion.div
                key={gi}
                className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                custom={gi * 0.5}
              >
                <div className={`${cores.light} px-6 py-4 border-b border-gray-100 flex items-center gap-3`}>
                  <div className={`${cores.bg} p-2 rounded-lg`}>
                    <GrupoIcon className={`h-5 w-5 ${cores.text}`} />
                  </div>
                  <h3 className="font-bold text-gray-900">{grupo.categoria}</h3>
                  <span className={`ml-auto text-xs font-medium ${cores.badge} px-2.5 py-1 rounded-full`}>
                    {grupo.items.length} {grupo.items.length === 1 ? 'entregavel' : 'entregaveis'}
                  </span>
                </div>
                <div className="divide-y divide-gray-50">
                  {grupo.items.map((item, ii) => (
                    <div key={ii} className="px-6 py-4 flex items-start gap-3">
                      <CheckCircle2 className={`h-5 w-5 ${cores.text} shrink-0 mt-0.5`} />
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">{item.nome}</p>
                        <p className="text-sm text-gray-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Cronograma */}
      <section className="bg-white py-16 border-y border-gray-100">
        <div className="mx-auto max-w-4xl px-4">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Cronograma de implantacao
          </motion.h2>
          <motion.p
            className="text-center text-gray-500 mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            12 semanas do inicio a publicacao completa
          </motion.p>

          <div className="relative">
            {/* Linha vertical */}
            <div className="absolute left-6 md:left-8 top-0 bottom-0 w-0.5 bg-gray-200" />

            <div className="space-y-8">
              {cronograma.map((fase, i) => {
                const cores = corClasses[fase.cor]
                return (
                  <motion.div
                    key={i}
                    className="relative pl-16 md:pl-20"
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    custom={i}
                  >
                    {/* Bolinha */}
                    <div className={`absolute left-3.5 md:left-5.5 w-5 h-5 rounded-full ${cores.btn} border-4 border-white shadow-md`} />

                    <div className={`${cores.light} rounded-xl p-5 border ${cores.border}`}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className={`text-xs font-bold ${cores.badge} px-2.5 py-1 rounded-full`}>{fase.semana}</span>
                        <h4 className="font-bold text-gray-900">{fase.fase}</h4>
                      </div>
                      <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {fase.items.map((item, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                            <Check className={`h-4 w-4 ${cores.text} shrink-0`} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Comparativo de Valor */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          O valor real desse projeto
        </motion.h2>
        <motion.p
          className="text-center text-gray-500 mb-10 max-w-2xl mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Se voce contratasse cada servico separadamente no mercado, gastaria entre {totalMercado}.
        </motion.p>

        <motion.div
          className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          <div className="divide-y divide-gray-50">
            {comparativo.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-6 py-4">
                <div className="flex items-center gap-3">
                  <Check className="h-4 w-4 text-rose-500 shrink-0" />
                  <span className="text-sm text-gray-700">{item.item}</span>
                </div>
                <span className="text-sm text-gray-400 font-medium whitespace-nowrap ml-4">{item.valorMercado}</span>
              </div>
            ))}
          </div>
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <span className="font-bold text-gray-900">Valor total no mercado</span>
            <span className="font-bold text-gray-400 line-through">{totalMercado}</span>
          </div>
          <div className="bg-rose-50 px-6 py-5 flex items-center justify-between">
            <span className="font-bold text-rose-900 text-lg">Seu investimento</span>
            <div className="text-right">
              <span className="font-bold text-rose-700 text-2xl">R$ 20.000</span>
              <p className="text-xs text-rose-600">R$ 10.000 de entrada + 5x R$ 2.000/semana</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Prova Social */}
      <section className="bg-gray-900 py-16">
        <div className="mx-auto max-w-4xl px-4">
          <motion.h2
            className="text-2xl md:text-3xl font-bold text-white text-center mb-10"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={0}
          >
            Construido por quem ja entregou resultados
          </motion.h2>

          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={1}
          >
            {[
              { num: '6', label: 'Livros publicados', icon: BookOpen },
              { num: '140+', label: 'Aulas produzidas', icon: GraduationCap },
              { num: '3', label: 'Plataformas online', icon: Monitor },
              { num: '60+', label: 'Artigos de blog', icon: FileText },
            ].map((stat, i) => {
              const Icon = stat.icon
              return (
                <div key={i} className="bg-gray-800 rounded-xl p-5 text-center border border-gray-700">
                  <Icon className="h-6 w-6 text-rose-400 mx-auto mb-2" />
                  <p className="text-3xl font-bold text-white">{stat.num}</p>
                  <p className="text-sm text-gray-400 mt-1">{stat.label}</p>
                </div>
              )
            })}
          </motion.div>

          <motion.div
            className="mt-8 bg-gray-800 rounded-2xl p-6 md:p-8 border border-gray-700"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            custom={2}
          >
            <div className="flex items-start gap-4">
              <div className="bg-rose-500/20 rounded-full p-3 shrink-0">
                <Star className="h-6 w-6 text-rose-400" />
              </div>
              <div>
                <p className="text-gray-300 leading-relaxed mb-3">
                  A plataforma que voce vai receber e baseada no mesmo sistema que roda o eBuz
                  (app.ebuz.com.br) e o Negocios S/A (app.antoniofogaca.com.br), com area de membros,
                  cursos publicados, blog, integracoes de pagamento e gestao de usuarios.
                  Nao e prototipo. E sistema de producao rodando em clientes reais.
                </p>
                <p className="text-sm text-rose-400 font-medium">
                  Jonathan Machado - Arquiteto de Software, eBuz Negocios Digitais
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Garantias */}
      <section className="mx-auto max-w-4xl px-4 py-16">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-10"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Suas garantias
        </motion.h2>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {[
            {
              icon: Shield,
              titulo: 'Aprovacao por etapa',
              desc: 'Cada fase do cronograma precisa da sua aprovacao antes de avancar. Voce tem controle total sobre o que e entregue.',
            },
            {
              icon: Award,
              titulo: 'Codigo aberto',
              desc: 'A plataforma e construida com tecnologias abertas (Next.js, React, Supabase). Voce nunca fica presa a um fornecedor.',
            },
            {
              icon: Heart,
              titulo: 'Suporte na implantacao',
              desc: 'Treinamento completo para voce operar tudo sozinha. Claude Code instalado para voce ter um assistente IA no dia a dia.',
            },
          ].map((item, i) => {
            const Icon = item.icon
            return (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
                <div className="bg-rose-50 rounded-xl p-3 w-fit mb-4">
                  <Icon className="h-6 w-6 text-rose-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{item.titulo}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            )
          })}
        </motion.div>
      </section>

      {/* Planos de Pagamento */}
      <section className="mx-auto max-w-3xl px-4 pb-16" id="investimento">
        <motion.h2
          className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-2"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Condicoes de pagamento
        </motion.h2>
        <motion.p
          className="text-center text-gray-500 mb-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Investimento total: R$ 20.000
        </motion.p>
        <motion.div
          className="bg-rose-50 border border-rose-200 rounded-xl p-4 mb-8 max-w-md mx-auto"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <p className="text-sm text-rose-800 font-medium text-center">
            R$ 10.000 de entrada agora + 5 parcelas semanais de R$ 2.000
          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {/* Card 1: Entrada PIX - R$ 10.000 */}
          {planos.map((plano) => {
            const cores = corClasses[plano.cor]
            const Icon = plano.icon
            return (
              <div
                key={plano.id}
                className={`rounded-2xl bg-white p-6 flex flex-col border-2 ${cores.border} shadow-lg relative`}
              >
                <span className={`absolute -top-3 left-1/2 -translate-x-1/2 ${cores.badge} text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap`}>
                  {plano.badge}
                </span>

                <div className={`inline-flex items-center justify-center rounded-lg ${cores.light} p-2 w-fit mb-3`}>
                  <Icon className={`h-5 w-5 ${cores.text}`} />
                </div>

                <h3 className="font-bold text-gray-900 text-lg">{plano.nome}</h3>
                <p className="text-sm text-gray-500 mb-4">{plano.subtitulo}</p>

                <div className="mb-1">
                  <span className="text-3xl font-bold text-gray-900">R$ {plano.preco.toLocaleString('pt-BR')}</span>
                </div>
                <p className="text-xs text-gray-400 mb-6">
                  PIX ou boleto bancario
                </p>

                <div className="flex-1" />

                <ul className="space-y-2 mb-6">
                  {[
                    'Todos os entregaveis inclusos',
                    'Cronograma de 12 semanas',
                    'Aprovacao por etapa',
                    'Codigo aberto (sem lock-in)',
                  ].map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                      <Check className={`h-4 w-4 ${cores.text} shrink-0`} />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href="https://wa.me/5561999999999?text=Oi%20Jonathan%2C%20quero%20iniciar%20o%20projeto%20Marca%20Vivo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full ${cores.btn} ${cores.btnHover} text-white py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] cursor-pointer text-center block`}
                >
                  Pagar entrada via PIX
                </a>
              </div>
            )
          })}

          {/* Card 2: Assinatura Semanal - 5x R$ 2.000 */}
          <div className="rounded-2xl bg-white p-6 flex flex-col border-2 border-blue-200 relative">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
              Restante do projeto
            </span>

            <div className="inline-flex items-center justify-center rounded-lg bg-blue-50 p-2 w-fit mb-3">
              <Calendar className="h-5 w-5 text-blue-700" />
            </div>

            <h3 className="font-bold text-gray-900 text-lg">Parcelas Semanais</h3>
            <p className="text-sm text-gray-500 mb-4">5 parcelas automaticas apos a entrada</p>

            <div className="mb-1">
              <span className="text-3xl font-bold text-gray-900">5x R$ 2.000</span>
            </div>
            <p className="text-xs text-gray-400 mb-6">
              Cobrado semanalmente via Assas
            </p>

            <div className="flex-1" />

            <ul className="space-y-2 mb-6">
              {[
                'Cobranca automatica semanal',
                'Link seguro Assas',
                'Recibo por email',
                'Cancele quando quiser',
              ].map((item, j) => (
                <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                  <Check className="h-4 w-4 text-blue-700 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <a
              href={ASSINATURA_SEMANAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3.5 rounded-xl font-semibold text-sm transition-all active:scale-[0.98] cursor-pointer text-center block"
            >
              Ativar parcelas semanais
            </a>
          </div>
        </motion.div>
      </section>

      {/* Checkout placeholder */}
      <section className="mx-auto max-w-3xl px-4 pb-16" id="checkout">
        <motion.div
          className="bg-white rounded-2xl border-2 border-rose-200 p-8 text-center shadow-sm"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          <div className="bg-rose-50 rounded-full p-4 w-fit mx-auto mb-4">
            <MessageCircle className="h-8 w-8 text-rose-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Entre em contato para iniciar o projeto</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Clique no botao abaixo para conversar diretamente pelo WhatsApp e alinhar os proximos passos.
          </p>
          <a
            href="https://wa.me/5561999999999?text=Oi%20Jonathan%2C%20quero%20iniciar%20o%20projeto%20Marca%20Vivo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-xl bg-rose-600 hover:bg-rose-700 px-8 py-4 text-white font-semibold text-lg active:scale-[0.98] transition-all cursor-pointer"
          >
            Falar pelo WhatsApp
            <ArrowRight className="h-5 w-5" />
          </a>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-4 pb-20">
        <motion.h2
          className="text-2xl font-bold text-gray-900 text-center mb-8"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={0}
        >
          Perguntas frequentes
        </motion.h2>

        <motion.div
          className="space-y-3"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          custom={1}
        >
          {faq.map((item, i) => (
            <div key={i} className="rounded-xl bg-white border border-gray-100 overflow-hidden">
              <button
                onClick={() => setFaqAberto(faqAberto === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="font-medium text-gray-900">{item.pergunta}</span>
                {faqAberto === i ? (
                  <ChevronUp className="h-5 w-5 text-gray-400 shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 shrink-0" />
                )}
              </button>
              {faqAberto === i && (
                <div className="px-5 pb-4">
                  <p className="text-gray-600 leading-relaxed">{item.resposta}</p>
                </div>
              )}
            </div>
          ))}
        </motion.div>
      </section>

      {/* CTA Final */}
      <section className="bg-rose-600 py-12 text-center">
        <div className="mx-auto max-w-2xl px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Pronta para construir seu ecossistema?
          </h2>
          <p className="text-rose-200 mb-6">
            Em 12 semanas voce vai ter plataforma, curso, livro, blog, podcast e funil.
            Tudo rodando, tudo seu, tudo pronto para faturar.
          </p>
          <button
            onClick={() => {
              document.getElementById('investimento')?.scrollIntoView({ behavior: 'smooth' })
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-4 text-rose-700 font-semibold text-lg hover:bg-rose-50 active:scale-[0.98] transition-all cursor-pointer"
          >
            Ver condicoes de pagamento
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-400">
        <a href="https://app.ebuz.com.br" target="_blank" rel="noopener noreferrer" className="hover:text-rose-400 transition">
          eBuz Negócios Digitais - CNPJ 29.436.457/0001-25
        </a>
        <p className="mt-1">Todos os direitos reservados - 2026</p>
      </footer>
    </main>
  )
}
